// react functional component for managing user roles and permissions in the admin portal
import React, { useEffect, useState } from "react";
import { NavLink } from "react-router-dom";
import typo from "@/styles/Typography.module.css";
import { useAuthenticatedApi } from "@/utils/callApi";
import { BrandButton as Button, SearchableSelect, Toast } from "@/components";
import useToast from "@/components/useToast";

export default function ManageRoles() {
    const { secureHelpers, isLoading: apiLoading } = useAuthenticatedApi();
    
    // Roles state
    const [roles, setRoles] = useState([]);
    const [newRoleId, setNewRoleId] = useState("");
    const [newRoleName, setNewRoleName] = useState("");
    const [newRoleDescription, setNewRoleDescription] = useState("");
    const [isAddingRole, setIsAddingRole] = useState(false);
    const [hoveredRoleId, setHoveredRoleId] = useState(null);
    
    // Edit role state
    const [editingRoleId, setEditingRoleId] = useState(null);
    const [editRoleName, setEditRoleName] = useState("");
    const [editRoleDescription, setEditRoleDescription] = useState("");
    
    const { toast, showToast, hideToast } = useToast();

    // Load data on mount
    useEffect(() => {
      loadRoles();
    }, []); // Empty array = run once on mount only
      // Load welcome message from API
  const loadRoles = async () => {
    try {
      const data = await secureHelpers.get('/api/roles');
      setRoles(Array.isArray(data) ? data : []);
      console.log('Roles loaded:', data);
    } catch (error) {
      console.error('Failed to load roles:', error);
      showToast({ variant: 'error', message: 'Failed to load roles' });
    }
  };

  // Add new role
  const handleAddRole = async () => {
    if (!newRoleId.trim()) {
      showToast({ variant: 'error', message: 'Please enter a role ID' });
      return;
    }

    if (!newRoleName.trim()) {
      showToast({ variant: 'error', message: 'Please enter a role name' });
      return;
    }

    if (!newRoleDescription.trim()) {
      showToast({ variant: 'error', message: 'Please enter a role description' });
      return;
    }

    // Validate existing roles data integrity
    const invalidRole = roles.find(role => !role || !role.id || !role.name);
    if (invalidRole) {
      console.error('Corrupted role data detected:', invalidRole);
      showToast({ variant: 'error', message: 'Error: Existing roles data is corrupted. Please refresh the page.' });
      return;
    }

    // Check for duplicate IDs
    if (roles.some(role => role.id.toLowerCase() === newRoleId.trim().toLowerCase())) {
      showToast({ variant: 'error', message: 'A role with this ID already exists' });
      return;
    }

    // Check for duplicate names
    if (roles.some(role => role.name.toLowerCase() === newRoleName.trim().toLowerCase())) {
      showToast({ variant: 'error', message: 'A role with this name already exists' });
      return;
    }

    setIsAddingRole(true);
    try {
      // TODO: Replace with actual API call when available
      const newRole = await secureHelpers.put(`/api/roles/${encodeURIComponent(newRoleId.trim())}`, { 
        id: newRoleId.trim(),
        name: newRoleName.trim(),
        description: newRoleDescription.trim()
      });
     
      setRoles([...roles, newRole]);
      setNewRoleId("");
      setNewRoleName("");
      setNewRoleDescription("");
      showToast({ variant: 'success', message: `Role "${newRole.name}" added successfully` });
    } catch (error) {
      console.error('Failed to add role:', error);
      showToast({ variant: 'error', message: 'Failed to add role' });
    } finally {
      setIsAddingRole(false);
    }
  };

  // Delete role - shows confirmation toast with Cancel/Delete buttons
  const handleDeleteRole = (roleId) => {
    showToast({
      variant: 'warning',
      title: 'Confirm Delete',
      message: 'Are you sure you want to delete this role?',
      actions: [
        {
          label: 'Cancel',
          variant: 'secondary',
          onClick: () => hideToast()
        },
        {
          label: 'Delete',
          variant: 'danger',
          onClick: () => confirmDeleteRole(roleId)
        }
      ]
    });
  };

  // Actually delete the role after confirmation
  const confirmDeleteRole = async (roleId) => {
    hideToast(); // Close the confirmation toast
    
    try {
      await secureHelpers.delete(`/api/roles/${encodeURIComponent(roleId)}`, { confirmed: true });
      
      setRoles(roles.filter(role => role.id !== roleId));
      showToast({ variant: 'success', message: 'Role deleted successfully' });
    } catch (error) {
      console.error('Failed to delete role:', error);
      showToast({ variant: 'error', message: 'Failed to delete role' });
    }
  };

  // Start editing a role
  const handleEditRole = (role) => {
    setEditingRoleId(role.id);
    setEditRoleName(role.name);
    setEditRoleDescription(role.description);
  };

  // Cancel editing
  const handleCancelEdit = () => {
    setEditingRoleId(null);
    setEditRoleName("");
    setEditRoleDescription("");
  };

  // Update role
  const handleUpdateRole = async (roleId) => {
    if (!editRoleName.trim()) {
      showToast({ variant: 'error', message: 'Please enter a role name' });
      return;
    }

    if (!editRoleDescription.trim()) {
      showToast({ variant: 'error', message: 'Please enter a role description' });
      return;
    }

    // Check for duplicate names (excluding current role)
    if (roles.some(role => role.id !== roleId && role.name.toLowerCase() === editRoleName.trim().toLowerCase())) {
      showToast({ variant: 'error', message: 'A role with this name already exists' });
      return;
    }

    try {
      const updatedRole = await secureHelpers.put(`/api/roles/${encodeURIComponent(roleId)}`, {
        id: roleId,
        name: editRoleName.trim(),
        description: editRoleDescription.trim()
      });

      setRoles(roles.map(role => role.id === roleId ? updatedRole : role));
      showToast({ variant: 'success', message: `Role "${updatedRole.name}" updated successfully` });
      handleCancelEdit();
    } catch (error) {
      console.error('Failed to update role:', error);
      showToast({ variant: 'error', message: 'Failed to update role' });
    }
  };

  return (
    <div className="container-fluid py-4">
          <h1 style={{ color: "#F16D09", marginBottom: "2rem" }}>Manage Roles</h1>
    
          {/* Toast Notification */}
          <Toast {...toast} onClose={hideToast} />
    
          {/* First Row */}
          <div className="row g-4 mb-4">
       {/* Role Management Card - Full Width */}
        <div className="col-md-12">
          <div className="card p-3 h-100">
            <h4 style={{ marginTop: 0, color: "#F16D09" }}>Role Management</h4>

            {/* Add New Role */}
            <div className="mb-3">
              <label className="form-label">Add New Role</label>
              <div className="row g-2">
                <div className="col-md-3">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Role ID..."
                    value={newRoleId}
                    onChange={(e) => setNewRoleId(e.target.value)}
                    disabled={isAddingRole}
                  />
                </div>
                <div className="col-md-3">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Role name..."
                    value={newRoleName}
                    onChange={(e) => setNewRoleName(e.target.value)}
                    disabled={isAddingRole}
                  />
                </div>
                <div className="col-md-4">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Role description..."
                    value={newRoleDescription}
                    onChange={(e) => setNewRoleDescription(e.target.value)}
                    disabled={isAddingRole}
                  />
                </div>
                <div className="col-md-2">
                  <Button
                    onClick={handleAddRole}
                    disabled={isAddingRole || !newRoleId.trim() || !newRoleName.trim() || !newRoleDescription.trim()}
                    style={{ width: '100%' }}
                  >
                    {isAddingRole ? 'Adding...' : 'Add Role'}
                  </Button>
                </div>
              </div>
            </div>
            {/* Roles List */}
            <div>
              <label className="form-label">Edit or delete existing Roles</label>
              {roles.length === 0 ? (
                <p className="text-muted">No roles defined yet.</p>
              ) : (
                <ul className="list-group" style={{maxWidth: '1000px', overflowY: 'auto' }}>
                  {roles.map((role) => (
                    <li 
                      key={role.id} 
                      className="list-group-item"
                    >
                      {editingRoleId === role.id ? (
                        // Edit mode
                        <div className="d-flex gap-2 align-items-center">
                          <div className="flex-grow-1">
                            <div className="row g-2">
                              <div className="col-md-3">
                                <input
                                  type="text"
                                  className="form-control form-control-sm"
                                  value={editRoleName}
                                  onChange={(e) => setEditRoleName(e.target.value)}
                                  placeholder="Role name"
                                />
                              </div>
                              <div className="col-md-6">
                                <input
                                  type="text"
                                  className="form-control form-control-sm"
                                  value={editRoleDescription}
                                  onChange={(e) => setEditRoleDescription(e.target.value)}
                                  placeholder="Role description"
                                />
                              </div>
                              <div className="col-md-3">
                                <small className="text-muted">ID: {role.id}</small>
                              </div>
                            </div>
                          </div>
                          <div className="d-flex gap-1">
                            <button
                              className="btn btn-sm btn-success"
                              onClick={() => handleUpdateRole(role.id)}
                              title="Save changes"
                            >
                              ✓
                            </button>
                            <button
                              className="btn btn-sm btn-secondary"
                              onClick={handleCancelEdit}
                              title="Cancel"
                            >
                              ✕
                            </button>
                          </div>
                        </div>
                      ) : (
                        // View mode
                        <div className="row align-items-center">
                          <div className="col-3">
                            <div style ={{ fontSize: "var(--font-heading-sm)", color: hoveredRoleId === role.id ? "red" : "#2e1719"}}>{role.name}</div>
                          </div>
                          <div className="col-5">
                            <div style ={{ fontSize: "var(--font-base)", color: hoveredRoleId === role.id ? "red" : "#666" }}>{role.description}</div>
                          </div>
                          <div className="col-2">
                            <div style={{ fontSize: "var(--font-base)", color: hoveredRoleId === role.id ? "red" : "#666" }}>ID: {role.id}</div>
                          </div>
                          <div className="col-2 text-end">
                            <div className="d-flex gap-1 justify-content-end">
                            <button
                              className="btn btn-sm btn-outline-primary"
                              onClick={() => handleEditRole(role)}
                              title="Edit role"
                              style={{ height: '2.5rem', fontSize: 'var(--font-subtitle)' }}
                            >
                              ✎
                            </button>
                            <button
                              className="btn btn-sm btn-outline-danger"
                              onMouseEnter = { () => {setHoveredRoleId(role.id);}}
                              onMouseLeave = { () => {setHoveredRoleId(null);}}
                              onClick={() => handleDeleteRole(role.id)}
                              title="Delete role"
                              style={{ height: '2.5rem', fontSize: 'var(--font-subtitle)', lineHeight: '1' }}
                            >
                              ×
                            </button>
                            </div>
                          </div>
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
    </div>
    </div>
    </div>
  );
}   